/**
 * ProjectSelector - Standalone project selector for App Mode
 * Displays a clean project dropdown in the main content area
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, FolderOpen } from 'lucide-react';
import type { ProjectConfig } from '@/lib/types';

interface ProjectSelectorProps {
  projects: ProjectConfig[];
  selectedProjectId: string | null;
  onProjectSelect: (projectId: string | null) => void;
}

export const ProjectSelector: React.FC<ProjectSelectorProps> = ({
  projects,
  selectedProjectId,
  onProjectSelect,
}) => {
  const { t } = useTranslation();

  const selectedProject = projects.find((p) => p.id === selectedProjectId);

  return (
    <div className="mb-4">
      <div className="relative inline-block min-w-[200px]">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
          <FolderOpen className="w-4 h-4 text-slate-400" />
        </div>
        <select
          value={selectedProjectId || ''}
          onChange={(e) => onProjectSelect(e.target.value || null)}
          disabled={projects.length === 0}
          className="appearance-none w-full pl-10 pr-10 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg cursor-pointer hover:border-slate-300 hover:bg-slate-50 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none transition-colors disabled:cursor-default disabled:opacity-50 disabled:bg-slate-50"
        >
          <option value="">{t('projects.allProjects')}</option>
          {[...projects]
            .sort((a, b) => a.projectName.localeCompare(b.projectName))
            .map((project) => (
              <option key={project.id} value={project.id}>
                {project.projectName}
              </option>
            ))}
        </select>
        {projects.length > 0 && (
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        )}
      </div>
      {selectedProject && (
        <p className="mt-1.5 text-xs text-slate-500 ml-1">
          {t('projects.filteringBy')}: <span className="font-medium text-slate-700">{selectedProject.projectName}</span>
        </p>
      )}
    </div>
  );
};

export default ProjectSelector;
